<template>
  <v-container>
    <!-- Management Toolbar -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card elevation="2" class="pa-3">
          <div class="d-flex flex-wrap align-center">
            <span class="text-subtitle-1 font-weight-medium mr-4">Sensor Management</span>
            <v-spacer class="d-none d-md-flex" />
            <v-btn color="primary" class="mr-2 mb-2 mb-md-0" @click="openAreaDialog(null)">
              <v-icon left>add_location</v-icon>
              New Area
            </v-btn>
            <v-btn color="secondary" class="mb-2 mb-md-0" @click="openSensorDialog(null)">
              <v-icon left>sensors</v-icon>
              New Sensor
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card elevation="3">
          <v-card-text class="text-center">
            <v-icon large color="primary">business</v-icon>
            <div class="text-h4 mt-2">{{ areas.length }}</div>
            <div class="text-caption">Total Areas</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card elevation="3">
          <v-card-text class="text-center">
            <v-icon large color="success">check_circle</v-icon>
            <div class="text-h4 mt-2">{{ totalOnline }}</div>
            <div class="text-caption">Sensors Online</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card elevation="3">
          <v-card-text class="text-center">
            <v-icon large color="error">error</v-icon>
            <div class="text-h4 mt-2">{{ totalOffline }}</div>
            <div class="text-caption">Sensors Offline</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      v-for="area in filteredAreas"
      :key="area.area_id"
      class="mb-3 elevation-2 area-card"
    >
      <v-card-title class="primary white--text py-3" @click="viewArea(area)" style="cursor: pointer;">
        <v-icon dark class="mr-2">location_on</v-icon>
        <span>{{ area.area_name }}</span>
        <v-spacer />
        <v-chip small dark outlined class="mr-1">
          <v-icon small left>check_circle</v-icon>
          {{ area.sensors_online || 0 }}
        </v-chip>
        <v-chip small dark outlined class="mr-1" v-if="area.sensors_offline > 0">
          <v-icon small left>error</v-icon>
          {{ area.sensors_offline }}
        </v-chip>
        <v-chip small dark outlined class="mr-1" v-if="area.sensors_unknown > 0" color="warning">
          <v-icon small left>help</v-icon>
          {{ area.sensors_unknown }}
        </v-chip>
        <v-btn icon dark small class="ml-2" @click.stop="openAreaDialog(area)" title="Edit Area">
          <v-icon small>edit</v-icon>
        </v-btn>
        <v-btn icon dark small @click.stop="confirmDeleteArea(area)" title="Delete Area">
          <v-icon small>delete</v-icon>
        </v-btn>
        <v-icon dark class="ml-2">arrow_forward</v-icon>
      </v-card-title>
      <v-card-text>
        <v-row dense class="mt-2">
          <v-col cols="12" sm="6">
            <div class="text-caption text--secondary">Total Sensors</div>
            <div class="text-h6">{{ area.sensors_total || 0 }}</div>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="text-caption text--secondary">Uptime</div>
            <v-progress-linear
              :value="parseFloat(area.uptime_percentage || 0)"
              :color="getUptimeColor(area.uptime_percentage)"
              height="20"
              rounded
              class="mt-1"
            >
              <strong>{{ area.uptime_percentage || 0 }}%</strong>
            </v-progress-linear>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="loading" class="text-center pa-5">
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-3">Loading sensors...</div>
    </v-card>

    <v-card v-if="!loading && areas.length === 0" class="text-center pa-5">
      <v-icon size="64" color="grey">sensors_off</v-icon>
      <div class="text-h6 mt-3">No sensor areas found</div>
      <v-btn color="primary" class="mt-4" @click="openAreaDialog(null)">
        <v-icon left>add_location</v-icon>
        Create First Area
      </v-btn>
    </v-card>

    <!-- Dialogs -->
    <area-form-dialog
      :visible="areaDialogVisible"
      :area="selectedArea"
      @close="areaDialogVisible = false"
      @saved="onAreaSaved"
    />

    <sensor-form-dialog
      :visible="sensorDialogVisible"
      :sensor="selectedSensor"
      @close="sensorDialogVisible = false"
      @saved="onSensorSaved"
    />

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="error white--text">
          <v-icon dark class="mr-2">warning</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete area <strong>{{ areaToDelete ? areaToDelete.area_name : '' }}</strong>?
          <div class="mt-2 text-caption error--text" v-if="areaToDelete && areaToDelete.sensors_total > 0">
            This area has {{ areaToDelete.sensors_total }} sensors. Remove them first.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            :disabled="areaToDelete && areaToDelete.sensors_total > 0"
            @click="deleteArea"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {mapState} from 'vuex'
import axios from 'axios'
import store from '../store'
import AreaFormDialog from './AreaFormDialog.vue'
import SensorFormDialog from './SensorFormDialog.vue'

export default {
  name: 'SensorStatus',
  components: {
    AreaFormDialog,
    SensorFormDialog
  },
  data() {
    return {
      areaDialogVisible: false,
      sensorDialogVisible: false,
      selectedArea: null,
      selectedSensor: null,
      deleteDialog: false,
      areaToDelete: null,
      deleting: false
    }
  },
  mounted() {
    store.dispatch('loadAreas')
  },
  computed: {
    ...mapState({
      loading: 'sensorsLoading',
      search: 'search'
    }),
    ...mapState(['areas']),
    filteredAreas() {
      if (!this.search)
        return this.areas
      const searchLower = this.search.toLowerCase()
      return this.areas.filter(area => area.area_name.toLowerCase().includes(searchLower))
    },
    totalOnline() {
      return this.areas.reduce((sum, area) => sum + (area.sensors_online || 0), 0)
    },
    totalOffline() {
      return this.areas.reduce((sum, area) => sum + (area.sensors_offline || 0), 0)
    }
  },
  methods: {
    getUptimeColor(uptime) {
      const value = parseFloat(uptime || 0)
      if (value >= 95)
        return 'success'
      if (value >= 80)
        return 'warning'
      return 'error'
    },
    viewArea(area) {
      this.$router.push(`/sensors/area/${area.area_id}`)
    },
    openAreaDialog(area) {
      this.selectedArea = area
      this.areaDialogVisible = true
    },
    openSensorDialog(sensor) {
      this.selectedSensor = sensor
      this.sensorDialogVisible = true
    },
    onAreaSaved() {
      store.dispatch('loadAreas')
    },
    onSensorSaved() {
      store.dispatch('loadAreas')
    },
    confirmDeleteArea(area) {
      this.areaToDelete = area
      this.deleteDialog = true
    },
    async deleteArea() {
      if (!this.areaToDelete)
        return

      this.deleting = true
      try {
        await axios.delete(`/api/areas/${this.areaToDelete.area_id}`)
        this.deleteDialog = false
        this.areaToDelete = null
        store.dispatch('loadAreas')
      } catch (error) {
        console.error('Failed to delete area:', error)
        const message = (error.response && error.response.data && error.response.data.error) || 'Failed to delete area'
        // eslint-disable-next-line no-alert
        window.alert(message)
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 16px !important;
}
</style>
